import { Box, Button, Collapse, Divider, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getAlbums, getArtist, getArtistLogo, getArtistPhoto, getArtistPhotos, getCompilationsByArtist } from '../../components/apis/music-api';
import GridAlbum from '../../components/grid-album/GridAlbum';
import { format, getLocationLink, getLocationString } from '../../components/text-helper/text-helper';
import { Album as AlbumType } from '../../types/Album';
import { Compilation as CompilationType } from '../../types/Compilation';
import { Artist as ArtistType } from '../../types/Artist';
import { BeatLoader } from 'react-spinners';
import { AuthenticationContext } from '../../components/authentication/AuthenticationContext';
import { isNumeric } from '../../components/text-helper/text-helper';
import { FileResponse } from '../../types/FileResponse';
import ButtonBar from '../../components/button-bar/ButtonBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCircleNodes, faImages, faLink, faMinus, faObjectGroup, faPenToSquare, faPlay, faPlus } from '@fortawesome/free-solid-svg-icons';
import GridCompilation from '../../components/grid-compilation/GridCompilation';
import callApi from '../../components/call-api/call-api';
import { getDateString } from '../../components/date-helper/date-helper';
import { useVectorModal } from '../../hooks/useVectorModal';
import VectorModal from '../../modals/vector-modal/vector-modal';
import { PlayModes } from '../../types/PlayModes';
import { useMusicStore } from '../../components/store/store';
import PhotoDisplay from '../../components/photo-display/PhotoDisplay';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { PhotoRating } from '../../types/PhotoRating';

type ArtistProps = {
    letter: string,
    artistName: string,
}

const Artist = () => {
  const params = useParams<ArtistProps>();
  const { getAccessToken } = useContext(AuthenticationContext);
  const [artist, setArtist] = useState<ArtistType>();
  const [albums, setAlbums] = useState<AlbumType[]>();
  const [compilations, setCompilations] = useState<CompilationType[]>();
  const [photo, setPhoto] = useState<FileResponse>();
  const [currentPhoto, setCurrentPhoto] = useState<string>();
  const [photoLoading, setPhotoLoading] = useState(true);
  const [photos, setPhotos] = useState<string[]>([]);
  const [logo, setLogo] = useState<FileResponse>();
  const [open, setOpen] = useState(false);
  const { applyVector } = useVectorModal();
  const { setPlayMode, setArtistName, isPlaying } = useMusicStore();
  const [rating, setRating] = useLocalStorage('artist-photo-rating', PhotoRating.General);
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  
  useEffect(() => {
    setArtist(undefined);
    setAlbums([]);
    setCompilations([]);
    setPhoto(undefined);
    setPhotos([]);
    setCurrentPhoto(undefined);
    setLogo(undefined);
  
    callApi<ArtistType>(getArtist(getAccessToken(), params.artistName ?? ''), setArtist);
    callApi<AlbumType[]>(getAlbums(getAccessToken(), params.artistName ?? ''), setAlbums);
    callApi<CompilationType[]>(getCompilationsByArtist(getAccessToken(), params.artistName ?? ''), setCompilations);
    callApi<string[]>(getArtistPhotos(getAccessToken(), params.artistName ?? '', rating), setPhotos);
    callApi<FileResponse>(getArtistLogo(getAccessToken(), params.artistName ?? ''), setLogo);
  }, [params.letter, params.artistName, getAccessToken, rating]);

  const getStyle = (key: string) => {
    const style = artist?.styles?.filter(x => x.key === key);
    if (style !== undefined && style.length > 0) {
      return style[0].value;
    }

    return 'inherit'
  }

  const getDividerColor = () => {
    const style = artist?.styles?.filter(x => x.key === 'color');
    if (style !== undefined && style.length > 0 && style[0].value !== undefined && style[0].value !== '') {
      return style[0].value;
    }

    return 'black';
  }

  const loadPhoto = (photoName: string) => {
    callApi<FileResponse>(getArtistPhoto(getAccessToken(), params.artistName ?? '', photoName), setPhoto, setPhotoLoading);
    setCurrentPhoto(photoName);
  }

  const handlePreviousPhoto = () => {
    const currentIndex = photos.findIndex(x => x === currentPhoto);
    const previousIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
    const previousPhoto = photos[previousIndex];
    loadPhoto(previousPhoto);
  }

  const handleNextPhoto = () => {
    const currentIndex = photos.findIndex(x => x === currentPhoto);
    const nextIndex = currentIndex === photos.length - 1 ? 0 : currentIndex + 1;
    const nextPhoto = photos[nextIndex];
    loadPhoto(nextPhoto);
  }

  useEffect(() => {
    if (photos?.length > 0) {
      const randomIndex = Math.floor(Math.random() * photos.length);
      loadPhoto(photos[randomIndex]);
    }
  }, [photos]);

  const setJukeBox = (isSimilar: boolean) => {
    setArtistName(params.artistName ?? '');
    setPlayMode(isSimilar ? PlayModes.Similar : PlayModes.Artist);
  }

  return (
    <>
      <VectorModal />
      <ButtonBar 
        leftButtons={[
          <Button component={Link} variant="contained" size="small" to={'#'} onClick={() => setJukeBox(false)}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faPlay} className="icon"/>
            Play
          </Button>,
          <Button
            component={Link}
            variant="contained"
            size="small"
            to={'#'}
            onClick={() => setJukeBox(true)}
            disabled={isPlaying}
          >
            <FontAwesomeIcon fontSize="small" color="primary" icon={faPlay} className="icon"/>
            Play Similar
          </Button>,
        ]}
        rightButtons={[
          <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${(params.artistName ?? '')}/photos`}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faImages} className="icon"/>
            Photos
          </Button>,
          <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${(params.artistName ?? '')}/genres`}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faObjectGroup} className="icon"/>
            Genres
          </Button>,
          <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${(params.artistName ?? '')}/similar`}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faLink} className="icon"/>
            Similar
          </Button>,
          <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${(params.artistName ?? '')}/edit`}>
            <FontAwesomeIcon fontSize="small" color="primary" icon={faPenToSquare} className="icon"/>
            Edit
          </Button>
        ]}
      />
      <Stack direction="row">
        <Box sx={{ width: '100%' }}>
          <Stack direction={isSmall ? 'column' : 'row'} spacing={3}>
            <Box sx={{
              width: isSmall ? '100%' : 'calc(100% - 280px)',
              borderRadius: '8px',
              backgroundColor: getStyle('background-color'),
              color: getStyle('color'),
            }}
          >
            <Stack spacing={2} sx={{ p: '0 16px 16px 16px' }}>
              {logo !== undefined &&
                <Stack spacing={2} sx={{ display: 'flex' ,justifyContent:'center', alignItems:'center', pt: '16px' }}>
                  <Box
                    component="img"
                    alt={''} src={`data:image/jpeg;base64,${logo?.fileContents}`}
                    style={{ maxWidth: '100%' }}
                  />
                </Stack>
              }
              {logo === undefined &&              
                <Box>
                  <Typography variant='h5'>{format(artist?.name ?? '')}</Typography>
                </Box>
              }
              <Divider sx={{ marginTop: '6px', marginBottom: '6px', borderColor: getDividerColor(), opacity: '20%' }} />
              <Stack direction="row">
                <Box>
                  <IconButton 
                    sx={{ color: getStyle('color') }}
                    size="small"
                    onClick={() => setOpen(!open)}
                  >
                    <FontAwesomeIcon icon={open ? faMinus : faPlus} />
                  </IconButton >
                </Box>
                <Box>
                  <Typography variant='h6'>Biography</Typography>
                </Box>
              </Stack>
              <Box>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <Typography style={{ whiteSpace: "pre-wrap", textAlign: 'left', fontSize: '14px' }}>{artist?.profile}</Typography>
                </Collapse>
                <Divider sx={{ marginTop: '6px', marginBottom: '6px', borderColor: getDividerColor(), opacity: '20%' }} />
              </Box>
              {albums !== undefined && albums.length > 0 &&
                <>
                  <Box>
                    <Typography variant='h6'>{albums?.length} Recordings</Typography>
                  </Box>
                  <Box>
                    {albums?.map((album, index) => {
                      return (
                        <>
                          {index > 0 && <Divider sx={{ marginTop: '6px', marginBottom: '6px', borderColor: getDividerColor(), opacity: '20%' }} />}
                          <GridAlbum key={`album-${index}`} letter={params.letter ?? ''} artistName={params.artistName ?? ''} album={album} />
                        </>
                      );
                    })}
                  </Box>
                </>
              }
              {compilations !== undefined && compilations.length > 0 &&
                <>
                  <Box>
                    <Typography variant='h6'>{compilations?.length} Compilations</Typography>
                  </Box>
                  <Box>
                    {compilations?.map((compilation, index) => {
                      return (
                        <>
                          {index > 0 && <Divider sx={{ marginTop: '6px', marginBottom: '6px', borderColor: getDividerColor(), opacity: '20%' }} />}
                          <GridCompilation key={`album-${index}`} directory={compilation.directory ?? ''} compilation={compilation} />
                        </>
                      );
                    })}
                  </Box>
                </>
              }
            </Stack>
          </Box>
          <Box sx={{ width: isSmall ? '100%' : '280px' }}>
            <Stack spacing={2} alignContent="left" sx={{ minHeight: '180px' }}>
              {photos?.length > 1 &&
                <Box sx={{ width: '280px', background: '#d8d8d8', borderRadius: '8px' }}>
                  <Stack direction="row" sx={{ width: '280px' }}>
                    <Box sx={{ width: '140px', textAlign: 'left' }}>
                      <IconButton onClick={handlePreviousPhoto}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </IconButton>
                    </Box>
                    <Box sx={{ width: '140px', textAlign: 'right'}}>
                      <IconButton onClick={handleNextPhoto}>
                        <FontAwesomeIcon icon={faArrowRight} />
                      </IconButton>
                    </Box>
                  </Stack>
                </Box>
              }
              <PhotoDisplay photo={photo} photoLoading={photoLoading} />
              <BeatLoader
                loading={photoLoading}
                size={24}                
                aria-label="Loading Spinner"
                data-testid="loader"
                style={{ margin: 'auto', marginTop: '40px', marginBottom: '40px' }}
              />
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  style={{ minWidth: 0 }}
                  onClick={() => applyVector(artist)}>
                  <FontAwesomeIcon fontSize="large" color="primary" icon={faCircleNodes} className="icon" style={{ marginRight: 0 }} />
                </Button>
              </Box>
              {artist?.timeActive !== undefined && artist?.timeActive.length > 0 &&
                <Box>
                  <Typography variant='h6'>Time Active</Typography>
                  {artist?.timeActive?.map((timeAPeriod, index) =>
                    <Stack key={`genre-${index}`}>
                      <Typography variant='body1'>{getDateString(timeAPeriod)}</Typography>
                    </Stack>
                  )}
                </Box>
              }
              {artist?.location !== undefined &&
                <Box>
                  <Typography variant='h6'>Location</Typography>
                  <Stack key="location">
                    <Link to={getLocationLink(artist?.location)}>
                      {getLocationString(artist.location)}
                    </Link>
                  </Stack>
                </Box>
              }
              {artist?.genres !== undefined && artist?.genres.length > 0 &&
                <Box>
                  <Typography variant='h6'>Genres</Typography>
                  {artist?.genres?.map((genre, index) =>
                    <Stack key={`genre-${index}`}>
                      <Link to={`/genres/${genre.id}`}>{genre.name}</Link>
                    </Stack>
                  )}
                </Box>
              }
              {artist?.similarArtists !== undefined && artist?.similarArtists.length > 0 &&
                <Box>
                  <Typography variant='h6'>Similar Artists</Typography>
                  {artist?.similarArtists?.map((name, index) =>
                    <Stack key={`sim-artist-${index}`}>
                      <Link to={`/letters/${isNumeric(name[0]) ? '0 - 9' : name[0].toUpperCase()}/artists/${name}`}>{format(name)}</Link>
                    </Stack>
                  )}
                </Box>     
              }
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Stack>
    </>
  )
}

export default Artist;
